import TarotEffect from './tarot-effect.js';

export default class Fade extends TarotEffect {
	static effectName = 'fade';

	static defaultOptions = {
		fade: { blur: 10, scale: 0.1, xOffset: 0, yOffset: 0 },
	};

	static rules = {
		minSlidesPerView: 1,
		maxSlidesPerView: 1,
		loopBuffer: { left: 0, right: 1 },
	};

	constructor(ctx) {
		super(ctx);
		this.lastState = new WeakMap();
		this.#loadFadeOptions();
	}

	#loadFadeOptions() {
		const _ = this;
		const fadeOpts = _.ctx.store.getOptions().fade || {};
		_.blurAmount = fadeOpts.blur;
		_.scaleAmount = fadeOpts.scale;
		_.xOffset = fadeOpts.xOffset;
		_.yOffset = fadeOpts.yOffset;
	}

	reInit() {
		this.#loadFadeOptions();
		this.lastState = new WeakMap();
	}

	/**
	 * Main render function called every animation frame
	 * Uses the frame-based architecture with dependency injection for utilities
	 *
	 * @param {Object} frame - Complete frame object with all carousel data
	 * @param {Array} frame.slides - Sorted slides array with calculated positions
	 * @param {Object} frame.widths - Layout measurements
	 * @param {Object} frame.state - Carousel state
	 * @param {Object} frame.animation - Animation data including trackPosition
	 * @param {Object} frame.transformPoints - Named position points
	 * @param {Object} utils - Frame utilities for position calculations
	 * @param {Function} utils.isSlideInRange - Check if slide is in range with progress
	 */
	render(frame, utils) {
		const _ = this;
		const { slides, widths, animation } = frame;

		// Fade effect uses calculated slide width, track position is virtual only
		_.renderSlideWidth(widths.slide);
		// Note: renderTrackPosition() not called - track DOM position irrelevant for fade

		const lastState = _.lastState;

		for (let i = 0, n = slides.length; i < n; ++i) {
			const slide = slides[i];
			// Frame engine has already set trackPosition and centerPoint properties

			const hiddenLeft = utils.isSlideInRange(slide, 'L+', 'L1');
			if (hiddenLeft.isInRange) {
				if (lastState.get(slide) !== 'hiddenLeft') {
					_.applyHiddenLeftFilter(slide);
					lastState.set(slide, 'hiddenLeft');
				}
				continue;
			}

			const fadeLeft = utils.isSlideInRange(slide, 'CL1', 'L1');
			if (fadeLeft.isInRange) {
				const key = `fadeLeft:${Math.round(fadeLeft.percent * 100)}`;
				if (lastState.get(slide) !== key) {
					_.applyFadeLeftFilter(slide, fadeLeft.percent);
					lastState.set(slide, key);
				}
				continue;
			}

			const fadeRight = utils.isSlideInRange(slide, 'R1', 'CL1');
			if (fadeRight.isInRange) {
				if (lastState.get(slide) !== 'fadeRight') {
					_.applyFadeRightFilter(slide);
					lastState.set(slide, 'fadeRight');
				}
				continue;
			}

			const hiddenRight = utils.isSlideInRange(slide, 'R+', 'R1');
			if (hiddenRight.isInRange) {
				if (lastState.get(slide) !== 'hiddenRight') {
					_.applyHiddenRightFilter(slide);
					lastState.set(slide, 'hiddenRight');
				}
				continue;
			}
		}
	}

	applyHiddenLeftFilter(slide) {
		const _ = this;
		const style = slide.style;
		slide.hide();
		style.opacity = '0';
		style.zIndex = '1';
		style.filter = `blur(${_.blurAmount}px)`;
		style.transform = `scale(${1 + _.scaleAmount}) translateX(${_.xOffset}px) translateY(${_.yOffset}px)`;
	}

	applyFadeLeftFilter(slide, percent) {
		// account for subpixel rounding
		if (percent > 0.995) percent = 1;

		const _ = this;
		const remaining = 1 - percent;
		const blur = remaining * _.blurAmount;
		const scale = 1 + remaining * _.scaleAmount;
		const tx = remaining * _.xOffset;
		const ty = remaining * _.yOffset;

		const style = slide.style;
		style.opacity = String(percent);
		slide.show();
		style.zIndex = '1';
		style.filter = `blur(${blur}px)`;
		style.transform = `scale(${scale}) translateX(${tx}px) translateY(${ty}px)`;
	}

	applyFadeRightFilter(slide) {
		const style = slide.style;
		style.opacity = '1';
		slide.show();
		style.zIndex = '0';
		style.filter = 'blur(0px)';
		style.transform = 'scale(1) translateX(0px) translateY(0px)';
	}

	applyHiddenRightFilter(slide) {
		const style = slide.style;
		style.opacity = '0';
		slide.hide();
		style.zIndex = '0';
		style.filter = 'blur(0px)';
		style.transform = 'scale(1) translateX(0px) translateY(0px)';
	}

	destroy() {
		super.destroy();
		// slides are now DOM elements directly, not wrapper objects
		const slides = this.ctx.store.getSlides() || [];
		for (let i = 0; i < slides.length; i++) {
			const slide = slides[i];
			if (!slide || !slide.style) continue;
			slide.style.opacity = '';
			slide.show?.();
			slide.style.zIndex = '';
			slide.style.filter = '';
			slide.style.transform = '';
		}
		this.lastState = null;
	}
}
